// ABOUTME: Toast component for unsaved changes with save/reset actions
// ABOUTME: Shows different states: initial (unsaved), loading (saving), success (saved)

import type { FC } from "react"
import { Check } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CButton } from "./CButton"
import { CSpinnerInline } from "./CSpinnerInline"
import { InfoIcon } from "@shared/icons"
import { cn } from "@shared/lib/utils"

type SaveToastState = "initial" | "loading" | "success"

interface CSaveToastProps {
  show: boolean
  state?: SaveToastState
  onReset?: () => void
  onSave?: () => void
  isProcessing?: boolean
  className?: string
  /** Disable save button (e.g., when form has validation errors) */
  disableSave?: boolean
}

export const CSaveToast: FC<CSaveToastProps> = ({
  show,
  state: externalState,
  onReset,
  onSave,
  isProcessing = false,
  className,
  disableSave = false,
}) => {
  const { t } = useTranslation()

  // Use external state if provided, otherwise derive from isProcessing
  const state = externalState ?? (isProcessing ? "loading" : "initial")

  if (!show) return null

  return (
    <div
      className={cn(
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-50",
        "inline-flex h-10 items-center justify-center overflow-hidden rounded-full",
        "bg-white/95 backdrop-blur",
        "border border-black/10",
        "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.15)]",
        "transition-all duration-300 ease-out",
        className,
      )}
    >
      <div className="flex h-full items-center justify-between px-3">
        <div className="flex items-center gap-2 text-gray-900">
          {state === "loading" && (
            <>
              <CSpinnerInline
                loading
                size="sm"
                className="h-4 w-4"
              />
              <div className="text-[13px] font-normal leading-tight whitespace-nowrap">{t("common.saving")}</div>
            </>
          )}
          {state === "success" && (
            <>
              <div className="p-0.5 bg-emerald-500/10 rounded-full border border-emerald-500/20 flex items-center justify-center">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div className="text-[13px] font-normal leading-tight whitespace-nowrap">{t("common.changesSaved")}</div>
            </>
          )}
          {state === "initial" && (
            <>
              <InfoIcon
                size={18}
                color="#6B7280"
              />
              <div className="text-[13px] font-normal leading-tight whitespace-nowrap">
                {t("common.unsavedChanges")}
              </div>
            </>
          )}
        </div>

        {state === "initial" && (
          <div className="ml-3 flex items-center gap-2">
            <CButton
              onClick={onReset}
              color="light"
              size="xs"
              className="!h-7 !px-3 !rounded-full !text-[13px] !font-normal"
              label={t("common.reset")}
            />
            <CButton
              onClick={onSave}
              color="blue"
              size="xs"
              className="!h-7 !px-3 !rounded-full !text-[13px] !font-medium"
              label={t("common.save")}
              disabled={disableSave}
            />
          </div>
        )}
      </div>
    </div>
  )
}
